// src/pages/maintenance/QuickAddContractorDrawer.js
// Right-side slide-in panel for creating/editing a MaintenanceVendor, with the same field set as
// the SQF Hub contractor form (SqfContractorsPage.js) so "all the information required in the
// contractor form" is captured in one place. On a successful create, offers to also add the
// vendor to the SQF Hub contractor list (when the company has SQF enabled) — "every contractor
// has to pass through there to enter the factory." See docs/MAINTENANCE_MODULE.md.
import React, { useState } from 'react';
import maintenanceService from '../../services/maintenanceService';
import { usePermissions } from '../../context/PermissionContext';
import ConfirmModal from '../../components/ConfirmModal';

// Same list as SqfContractorsPage.js's TYPES — duplicated locally rather than cross-imported, to
// keep the Maintenance and SQF modules decoupled.
const TYPES = ['Pest control', 'Electrician', 'Plumber', 'Refrigeration', 'Equipment repair', 'Cleaning', 'Other'];

export default function QuickAddContractorDrawer({ open, vendor, onClose, onSaved }) {
  const { hasModule } = usePermissions();
  const [form, setForm] = useState({
    name: vendor?.name || '',
    contactName: vendor?.contactName || '',
    contactEmail: vendor?.contactEmail || '',
    contactPhone: vendor?.contactPhone || '',
    contractorType: vendor?.contractorType || '',
    notes: vendor?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [offerSqfPrompt, setOfferSqfPrompt] = useState(null); // the newly-created vendor, awaiting the SQF-hub decision

  // Reset form when a different vendor (or a fresh "new") is opened
  React.useEffect(() => {
    if (open) {
      setForm({
        name: vendor?.name || '', contactName: vendor?.contactName || '',
        contactEmail: vendor?.contactEmail || '', contactPhone: vendor?.contactPhone || '',
        contractorType: vendor?.contractorType || '', notes: vendor?.notes || '',
      });
      setError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, vendor?.id]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    // A solo contractor often has no separate "company" name — their own name functionally is
    // the business name. Require at least one of the two rather than forcing Company / Vendor
    // specifically (that's what silently blocked saving before: nothing was sent to the API at
    // all, so there was nothing to list and no SQF-hub prompt to trigger).
    if (!form.name.trim() && !form.contactName.trim()) {
      setError('Enter a company name or a contact name.');
      return;
    }
    setSaving(true); setError('');
    try {
      const payload = { ...form, name: form.name.trim() || form.contactName.trim() };
      const result = vendor?.id
        ? await maintenanceService.updateVendor(vendor.id, payload)
        : await maintenanceService.createVendor(payload);

      if (!vendor?.id && !result.sqfContractorId && hasModule('sqf.checklists.manage')) {
        setOfferSqfPrompt(result);
      } else {
        onSaved(result);
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to save vendor.');
    } finally {
      setSaving(false);
    }
  };

  const addToSqfHub = async () => {
    const vendorToSave = offerSqfPrompt;
    try {
      await maintenanceService.addVendorToSqfHub(vendorToSave.id);
    } catch {
      // Non-fatal — the vendor itself already saved successfully; the SQF-hub push is a bonus step.
    } finally {
      setOfferSqfPrompt(null);
      onSaved(vendorToSave);
    }
  };

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)',
          zIndex: 1049,
          opacity: open ? 1 : 0,
          pointerEvents: open ? 'auto' : 'none',
          transition: 'opacity 0.25s ease',
        }}
      />
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0,
        width: 480, maxWidth: '95vw',
        background: '#fff',
        display: 'flex', flexDirection: 'column',
        zIndex: 1050, boxShadow: '-4px 0 24px rgba(0,0,0,0.15)',
        overflow: 'hidden',
        transform: open ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        pointerEvents: open ? 'auto' : 'none',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', padding: '14px 18px', borderBottom: '1px solid #dee2e6', flexShrink: 0 }}>
          <span style={{ fontSize: 18, fontWeight: 600 }}>{vendor?.id ? 'Edit' : 'New'} Contractor</span>
          <button className="btn-close ms-auto" onClick={onClose} aria-label="Close" />
        </div>

        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflowY: 'auto', padding: 18 }}>
            {error && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{error}</div>}

            <div className="mb-3">
              <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Company / Vendor <span className="text-danger">*</span></label>
              <input className="form-control" value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. ACME Refrigeration" autoFocus />
            </div>
            <div className="mb-3">
              <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Contact Name</label>
              <input className="form-control" value={form.contactName} onChange={e => set('contactName', e.target.value)} placeholder="Who to call" />
            </div>
            <div className="mb-3">
              <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Email</label>
              <input type="email" className="form-control" value={form.contactEmail} onChange={e => set('contactEmail', e.target.value)} />
            </div>
            <div className="mb-3">
              <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Phone</label>
              <input className="form-control" value={form.contactPhone} onChange={e => set('contactPhone', e.target.value)} />
            </div>
            <div className="mb-3">
              <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Type</label>
              <select className="form-select" value={form.contractorType} onChange={e => set('contractorType', e.target.value)}>
                <option value="">Select…</option>
                {TYPES.map(tp => <option key={tp} value={tp}>{tp}</option>)}
              </select>
            </div>
            <div className="mb-3">
              <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Notes</label>
              <textarea className="form-control" rows={3} value={form.notes} onChange={e => set('notes', e.target.value)} />
            </div>

            {vendor?.sqfContractorId && (
              <div className="alert alert-info py-2" style={{ fontSize: 12 }}>✓ This contractor is also in the SQF Hub contractor list.</div>
            )}
          </div>

          <div style={{ padding: '14px 18px', borderTop: '1px solid #dee2e6', display: 'flex', justifyContent: 'flex-end', gap: 8, flexShrink: 0 }}>
            <button type="button" className="btn btn-outline-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save Contractor'}</button>
          </div>
        </form>
      </div>

      <ConfirmModal
        show={!!offerSqfPrompt}
        title="Add to SQF Hub?"
        message={`Also add "${offerSqfPrompt?.name}" to the SQF Hub contractor list? Every contractor entering the factory needs to be there for facility access and agreement tracking.`}
        confirmLabel="Add to SQF Hub"
        cancelLabel="Not now"
        variant="primary"
        onConfirm={addToSqfHub}
        onCancel={() => { const v = offerSqfPrompt; setOfferSqfPrompt(null); onSaved(v); }}
      />
    </>
  );
}
